import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, ExternalLink, Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_barber-membership-1/artifacts/lnz6qr8g_Screenshot_20260112_182038_Drive.jpg";
const VAGARO_URL = "https://www.vagaro.com/dbhgg6";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/membership", label: "Membership" },
  { to: "/contact", label: "Contact" },
];

export default function StickyNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="sticky-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || mobileOpen
          ? "bg-[#0A0A0B]/95 backdrop-blur-xl border-b border-white/5 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24 flex items-center justify-between">
        {/* Logo */}
        <Link to="/">
          <img
            src={LOGO_URL}
            alt="LUXGROOM"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={`text-sm tracking-wide transition-colors ${
                location.pathname === link.to
                  ? "text-[#C5A059] font-medium"
                  : "text-[#A1A1AA] hover:text-[#F3F4F6]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-4 md:gap-6">
          <a
            href="tel:4159946699"
            data-testid="nav-phone"
            className="hidden md:flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#C5A059] transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span>(415) 994-6699</span>
          </a>
          <a
            href={VAGARO_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-btn"
            className="bg-[#C5A059] text-black hover:bg-[#D4AF37] font-medium transition-all duration-300 rounded-sm px-5 py-2.5 text-xs uppercase tracking-wider flex items-center gap-2"
          >
            Book Now
            <ExternalLink className="w-3.5 h-3.5" />
          </a>

          {/* Mobile menu toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-[#F3F4F6] p-1"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="lg:hidden px-6 md:px-12 pt-4 pb-6 border-t border-white/5 mt-3 animate-fade-in">
          <div className="space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block text-sm py-2 transition-colors ${
                  location.pathname === link.to
                    ? "text-[#C5A059] font-medium"
                    : "text-[#A1A1AA] hover:text-[#F3F4F6]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="tel:4159946699"
              className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#C5A059] transition-colors py-2"
            >
              <Phone className="w-4 h-4" /> (415) 994-6699
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
